import React, { useEffect, useState } from "react";

// minimal relationship + gender sets; replace with your JSON if you like
const RELS = ["Self", "Child", "Other Relationship"];
const GENDERS = ["Female", "Male", "Prefer not to say"];

// simple date validation: MM/DD/YYYY (same pattern as PersonalInfoQuestion)
const validDate = (s) => {
    if (!s || s.length !== 10) return false;
    const [m, d, y] = s.split("/").map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    if (dt.getMonth() !== (m - 1) || dt.getDate() !== d || dt.getFullYear() !== y) return false;
    if (dt > new Date()) return false;
    if (y < 1900) return false;
    return true;
};

const InsuranceInfoQuestion = ({ onSubmit, onNext, initialValue }) => {
    const [memberID, setMemberID] = useState("");
    const [relationship, setRelationship] = useState("Self");

    const [fname, setFname] = useState("");
    const [lname, setLname] = useState("");
    const [gender, setGender] = useState("");
    const [dob, setDob] = useState(""); // MM/DD/YYYY

    const isSelf = relationship === "Self";
    const inputRef = React.useRef(null);

    useEffect(() => {
        if (!initialValue) return;
        setMemberID(initialValue.memberID || "");
        setRelationship(initialValue.policyOwner?.relationship || "Self");
        setFname(initialValue.policyOwner?.fname || "");
        setLname(initialValue.policyOwner?.lname || "");
        setGender(initialValue.policyOwner?.gender || "");
        setDob(initialValue.policyOwner?.dob || "");
    }, [initialValue]);

    // Delayed autofocus to allow smooth transition
    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus();
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const canSubmit =
        memberID.trim() &&
        relationship &&
        (isSelf || (fname.trim() && lname.trim() && gender && validDate(dob)));

    const handle = (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        onSubmit({
            memberID: memberID.trim(),
            policyOwner: {
                relationship,
                fname: isSelf ? "" : fname.trim(),
                lname: isSelf ? "" : lname.trim(),
                gender: isSelf ? "" : gender,
                dob: isSelf ? "" : dob, // keep MM/DD/YYYY; your payload builder can format to ISO
            },
        });
        onNext();

        // reset (optional)
        // setMemberID(""); setRelationship("Self"); setFname(""); setLname(""); setGender(""); setDob("");
    };

    // small helper mirroring your DOB auto-formatting behavior
    const formatDob = (value) => {
        const nums = value.replace(/\D/g, "").slice(0, 8); // MMDDYYYY
        if (nums.length >= 4) return `${nums.slice(0, 2)}/${nums.slice(2, 4)}/${nums.slice(4)}`;
        if (nums.length >= 2) return `${nums.slice(0, 2)}/${nums.slice(2)}`;
        return nums;
    };

    return (
        <form onSubmit={handle} className="input-form">
            <div
                className="input-field-container"
                style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 520 }}
            >
                <label className="input-label">Member ID</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="Enter your Member ID"
                    value={memberID}
                    onChange={(e) => setMemberID(e.target.value)}
                    ref={inputRef}
                />

                <label className="input-label" style={{ marginTop: 8 }}>Relationship to Policy Owner</label>
                <div className="treatment-options"
                    style={{
                        flexWrap: "wrap",
                        display: "flex",
                        gap: 8,
                    }}
                >
                    {RELS.map((r) => (
                        <button
                            key={r}
                            type="button"
                            className={`treatment-button ${relationship === r ? "treatment-button-selected" : ""}`}
                            onClick={(e) => { e.currentTarget.blur(); setRelationship(r); }}
                        >
                            {r}
                        </button>
                    ))}
                </div>

                {!isSelf && (
                    <>
                        <div className="dual-input-container dual-no-stack">
                            <div className="input-group">
                                <label className="input-label">Policy Owner First Name</label>
                                <input
                                    type="text"
                                    className="input-field"
                                    value={fname}
                                    onChange={(e) => setFname(e.target.value)}
                                    placeholder="First name"
                                />
                            </div>
                            <div className="input-group" >
                                <label className="input-label">Policy Owner Last Name</label>
                                <input
                                    type="text"
                                    className="input-field"
                                    value={lname}
                                    onChange={(e) => setLname(e.target.value)}
                                    placeholder="Last name"
                                />
                            </div>
                        </div>

                        <label className="input-label" style={{ marginTop: 8 }}>Policy Owner Gender</label>
                        <div className="treatment-options"
                            style={{
                                flexWrap: "wrap",
                                display: "flex",
                                gap: 8,
                            }}
                        >
                            {GENDERS.map((g) => (
                                <button
                                    key={g}
                                    type="button"
                                    className={`treatment-button ${gender === g ? "treatment-button-selected" : ""}`}
                                    onClick={(e) => { e.currentTarget.blur(); setGender(g); }}
                                >
                                    {g}
                                </button>
                            ))}
                        </div>

                        <label className="input-label" style={{ marginTop: 8 }}>Policy Owner Date of Birth</label>
                        <input
                            type="text"
                            className={`input-field ${dob && !validDate(dob) ? "input-error" : ""}`}
                            placeholder="MM/DD/YYYY"
                            maxLength={10}
                            value={dob}
                            onChange={(e) => setDob(formatDob(e.target.value))}
                        />
                        {dob && !validDate(dob) && <div className="error-message">Enter a valid date in the past.</div>}
                    </>
                )}
                <button
                    type="submit"
                    className="state-submit-button"
                    disabled={!canSubmit}
                >
                    Continue
                </button>
            </div>
        </form>
    );
};

export default InsuranceInfoQuestion;
